import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { CheckCircle2, Phone } from 'lucide-react';
export function FlooringAndLightingPage() {
  return <div className="flex flex-col min-h-screen pt-24 pb-20" data-id="element-340">
      {/* Hero */}
      <section className="relative bg-gray-50 py-16 md:py-24 overflow-hidden" data-id="element-341">
        <div className="absolute inset-0 z-0" data-id="element-342">
          <img src="https://images.unsplash.com/photo-1556911220-bff31c812dba?auto=format&fit=crop&q=80&w=2000" alt="Bathroom flooring and lighting" className="w-full h-full object-cover object-center" data-id="element-343" />
          <div className="absolute inset-0 bg-white/85" data-id="element-344"></div>
        </div>

        <div className="container relative z-10 mx-auto px-4 md:px-6" data-id="element-345">
          <div className="max-w-3xl mx-auto text-center" data-id="element-346">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-347">
              Flooring & Lighting Services in Northern Indiana
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-348">
              Professional bathroom flooring installation and lighting upgrades
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-349">
              <Link to="/contact" data-id="element-350">
                <Button size="lg" data-id="element-351">Get Free Consultation</Button>
              </Link>
              <Button variant="outline" size="lg" data-id="element-352">
                <Phone className="mr-2 h-5 w-5" data-id="element-353" />
                (574) 914-1522
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16" data-id="element-354">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl" data-id="element-355">
          <div className="prose prose-lg max-w-none" data-id="element-356">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-357">
              Transform Your Bathroom with New Flooring and Lighting
            </h2>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-358">
              The right flooring and lighting can completely transform your
              bathroom's appearance and functionality. Lucky Duck Bathroom
              Company provides expert flooring installation and lighting
              upgrades throughout Northern Indiana, helping homeowners create
              beautiful, practical bathrooms that enhance daily comfort and home
              value. From durable, water-resistant flooring to well-designed
              lighting that improves visibility and ambiance, we deliver quality
              results that make a real difference.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-359">
              Bathroom Flooring Options
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-360">
              Bathroom flooring must withstand moisture, humidity, and daily
              wear while looking great and providing safe footing. We install a
              variety of flooring materials suited to bathroom environments,
              each offering unique benefits for different needs and budgets.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-6" data-id="element-361">
              <h4 className="text-xl font-bold text-gray-900 mb-4" data-id="element-362">
                Flooring Materials We Install:
              </h4>
              <ul className="space-y-3" data-id="element-363">
                {['Ceramic tile - classic, durable, water-resistant, endless design options', 'Porcelain tile - extremely durable, low maintenance, ideal for high-traffic bathrooms', 'Luxury vinyl plank (LVP) - waterproof, comfortable underfoot, wood-look appeal', 'Natural stone - marble, travertine, slate for elegant, timeless beauty', 'Heated flooring systems - radiant warmth for ultimate comfort'].map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-364">
                    <CheckCircle2 className="h-6 w-6 text-[#8BA888] shrink-0 mt-0.5" data-id="element-365" />
                    <span className="text-gray-700" data-id="element-366">{item}</span>
                  </li>)}
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-367">
              Professional Flooring Installation
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-368">
              Proper flooring installation is critical in bathrooms where
              moisture exposure is constant. We begin with thorough subfloor
              preparation, ensuring surfaces are level, clean, and properly
              sealed. For tile installations, we use appropriate underlayment
              and waterproofing membranes to prevent water damage to subfloors.
              Vinyl installations require smooth, flat surfaces for proper
              adhesion and appearance.
            </p>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-369">
              Our experienced team pays careful attention to transitions between
              rooms, ensuring smooth, professional-looking connections. We work
              around existing fixtures when possible or coordinate removal and
              reinstallation as needed. Every installation includes proper
              sealing and finishing to protect against moisture infiltration and
              ensure long-lasting performance.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-370">
              Bathroom Lighting Solutions
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-371">
              Good lighting is essential for bathroom functionality and safety.
              Proper illumination makes grooming tasks easier, prevents
              accidents, and creates the right ambiance for relaxation. We
              design lighting plans that combine different types of fixtures to
              provide adequate illumination for all bathroom activities.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-6" data-id="element-372">
              <h4 className="text-xl font-bold text-gray-900 mb-4" data-id="element-373">
                Lighting Options We Install:
              </h4>
              <ul className="space-y-3" data-id="element-374">
                {['Vanity lighting - task lighting for grooming and makeup application', 'Recessed ceiling lights - general ambient lighting', 'Shower and tub lighting - safe illumination for bathing areas', 'Exhaust fans with integrated lighting - ventilation and light combined', 'Dimmer switches - adjustable lighting for different moods and times', 'LED fixtures - energy-efficient, long-lasting, excellent light quality'].map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-375">
                    <CheckCircle2 className="h-6 w-6 text-[#8BA888] shrink-0 mt-0.5" data-id="element-376" />
                    <span className="text-gray-700" data-id="element-377">{item}</span>
                  </li>)}
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-378">
              Layered Lighting Design
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-379">
              The best bathroom lighting combines multiple layers: ambient
              lighting for overall illumination, task lighting focused on
              specific areas like vanities, and accent lighting to highlight
              architectural features or create atmosphere. We help you select
              fixtures and placement that provide adequate light for safety and
              function while creating the aesthetic you desire.
            </p>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-380">
              Modern LED technology offers excellent light quality with
              significant energy savings and long lifespans. We can install
              fixtures with adjustable color temperature, allowing you to choose
              warm, relaxing light for evening baths or bright, energizing light
              for morning routines. Dimmer controls add flexibility, letting you
              adjust lighting levels to suit different activities and
              preferences.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-381">
              Coordinating Flooring and Lighting
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-382">
              Flooring and lighting work together to define your bathroom's
              overall look and feel. Light-colored flooring reflects more light,
              making small bathrooms feel larger and brighter. Darker flooring
              creates dramatic contrast and hides dirt better but may require
              more lighting. We help you coordinate these elements to achieve
              your desired aesthetic while ensuring practical functionality.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-383">
              Safety and Code Compliance
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-384">
              Bathroom electrical work must meet strict safety codes due to
              moisture exposure. All lighting installations are performed by
              licensed professionals following local electrical codes. We use
              GFCI-protected circuits, proper fixture ratings for wet locations,
              and appropriate installation methods to ensure your safety.
              Flooring installations include slip-resistant options and proper
              transitions to prevent tripping hazards.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-385">
              Why Choose Lucky Duck?
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-386">
              As a family-owned business serving Northern Indiana, we bring
              local expertise and personalized service to every flooring and
              lighting project. We understand the unique challenges of bathroom
              environments and use quality materials and proven installation
              methods to deliver lasting results. Our team works efficiently,
              keeps job sites clean, and completes projects on schedule with
              minimal disruption to your routine.
            </p>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-387">
              We serve South Bend, Mishawaka, Elkhart, Goshen, Granger, and
              surrounding communities with honest pricing, superior workmanship,
              and the attention to detail you deserve. Whether you're updating
              flooring, upgrading lighting, or tackling both as part of a larger
              renovation, we're committed to exceeding your expectations.
            </p>

            <div className="bg-[#8BA888]/10 border-l-4 border-[#8BA888] p-6 rounded-r-lg mt-8" data-id="element-388">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-389">
                Ready to Upgrade Your Bathroom Flooring and Lighting?
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-390">
                Contact Lucky Duck Bathroom Company for a free consultation and
                let's discuss how new flooring and lighting can transform your
                bathroom.
              </p>
              <Link to="/contact" data-id="element-391">
                <Button size="lg" data-id="element-392">Schedule Free Consultation</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
}